import React, { useState, useEffect } from 'react';
import {
  Box, Table, Tbody, Td, Text, Th, Thead, Tr, useColorModeValue,
  Badge, Flex, Button, Modal, ModalOverlay, ModalContent,
  ModalHeader, ModalBody, ModalFooter, ModalCloseButton, FormControl,
  FormLabel, Input, NumberInput, NumberInputField, Switch, Textarea,
  useDisclosure, useToast, Grid, HStack,
} from '@chakra-ui/react';
import Card from 'components/card/Card';
import { MdAdd, MdEdit, MdMeetingRoom } from 'react-icons/md';
import { getClassrooms, createClassroom, updateClassroom } from '../../services/academic.service';

export default function ClassroomsView() {
  const borderColor = useColorModeValue('gray.200', 'whiteAlpha.100');
  const rowHover = useColorModeValue('gray.50', 'whiteAlpha.50');
  const toast = useToast();
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editMode, setEditMode] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({ name: '', building: '', floorNumber: '', capacity: 30, hasProjector: false, hasComputer: false, hasWhiteboard: true, notes: '' });

  useEffect(() => { load(); }, []);
  const load = async () => { try { setLoading(true); const r = await getClassrooms(); setRooms(r?.payload?.data || []); } catch (e) { console.error(e); } finally { setLoading(false); } };

  const openCreate = () => { setEditMode(false); setEditId(null); setForm({ name: '', building: '', floorNumber: '', capacity: 30, hasProjector: false, hasComputer: false, hasWhiteboard: true, notes: '' }); onOpen(); };
  const openEdit = (r) => { setEditMode(true); setEditId(r.id); setForm({ name: r.name, building: r.building || '', floorNumber: r.floor_number || '', capacity: r.capacity, hasProjector: !!r.has_projector, hasComputer: !!r.has_computer, hasWhiteboard: !!r.has_whiteboard, notes: r.notes || '' }); onOpen(); };

  const handleSave = async () => {
    if (!form.name) { toast({ title: 'Nombre requerido', status: 'warning', duration: 3000 }); return; }
    try {
      if (editMode) await updateClassroom(editId, form);
      else await createClassroom(form);
      toast({ title: editMode ? 'Aula actualizada' : 'Aula creada', status: 'success', duration: 3000 });
      onClose(); load();
    } catch (e) { toast({ title: 'Error', status: 'error', duration: 3000 }); }
  };

  if (loading) return null;

  return (
    <Box pt={{ base: '130px', md: '80px', xl: '80px' }}>
      <Card p={4}>
        <Flex justify="space-between" align="center" mb={4}>
          <Text fontSize="lg" fontWeight="700">Aulas ({rooms.length})</Text>
          <Button leftIcon={<MdAdd />} colorScheme="brand" onClick={openCreate}>Nueva aula</Button>
        </Flex>
        <Box overflowX="auto"><Table size="sm" variant="simple"><Thead><Tr><Th borderColor={borderColor}>Nombre</Th><Th borderColor={borderColor}>Edificio</Th><Th borderColor={borderColor}>Piso</Th><Th borderColor={borderColor}>Capacidad</Th><Th borderColor={borderColor}>Equipamiento</Th><Th borderColor={borderColor}>Acciones</Th></Tr></Thead><Tbody>
          {rooms.map(r => (
            <Tr key={r.id} _hover={{ bg: rowHover }}>
              <Td borderColor={borderColor}><Text fontWeight="600">{r.name}</Text></Td>
              <Td borderColor={borderColor}>{r.building || '-'}</Td>
              <Td borderColor={borderColor}>{r.floor_number || '-'}</Td>
              <Td borderColor={borderColor}><Badge>{r.capacity}</Badge></Td>
              <Td borderColor={borderColor}><HStack spacing={1}>
                {r.has_projector ? <Badge colorScheme="blue" fontSize="2xs">Proyector</Badge> : null}
                {r.has_computer ? <Badge colorScheme="purple" fontSize="2xs">PC</Badge> : null}
                {r.has_whiteboard ? <Badge colorScheme="green" fontSize="2xs">Pizarra</Badge> : null}
              </HStack></Td>
              <Td borderColor={borderColor}><Button size="xs" variant="ghost" leftIcon={<MdEdit />} onClick={() => openEdit(r)}>Editar</Button></Td>
            </Tr>
          ))}
        </Tbody></Table></Box>
      </Card>
      <Modal isOpen={isOpen} onClose={onClose} size="lg"><ModalOverlay /><ModalContent>
        <ModalHeader>{editMode ? 'Editar aula' : 'Nueva aula'}</ModalHeader><ModalCloseButton />
        <ModalBody pb={6}>
          <Grid templateColumns="1fr 1fr" gap={3}>
            <FormControl isRequired><FormLabel>Nombre</FormLabel><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ej: Salón 101" /></FormControl>
            <FormControl><FormLabel>Edificio</FormLabel><Input value={form.building} onChange={e => setForm({ ...form, building: e.target.value })} /></FormControl>
            <FormControl><FormLabel>Piso</FormLabel><Input value={form.floorNumber} onChange={e => setForm({ ...form, floorNumber: e.target.value })} /></FormControl>
            <FormControl><FormLabel>Capacidad</FormLabel><NumberInput min={1} value={form.capacity} onChange={v => setForm({ ...form, capacity: v })}><NumberInputField /></NumberInput></FormControl>
          </Grid>
          <Grid templateColumns="1fr 1fr 1fr" gap={3} mt={4}>
            <FormControl display="flex" alignItems="center"><FormLabel mb="0" fontSize="sm">Proyector</FormLabel><Switch isChecked={form.hasProjector} onChange={e => setForm({ ...form, hasProjector: e.target.checked })} /></FormControl>
            <FormControl display="flex" alignItems="center"><FormLabel mb="0" fontSize="sm">PC</FormLabel><Switch isChecked={form.hasComputer} onChange={e => setForm({ ...form, hasComputer: e.target.checked })} /></FormControl>
            <FormControl display="flex" alignItems="center"><FormLabel mb="0" fontSize="sm">Pizarra</FormLabel><Switch isChecked={form.hasWhiteboard} onChange={e => setForm({ ...form, hasWhiteboard: e.target.checked })} /></FormControl>
          </Grid>
          <FormControl mt={3}><FormLabel>Notas</FormLabel><Textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} rows={2} /></FormControl>
        </ModalBody>
        <ModalFooter><Button mr={3} onClick={onClose}>Cancelar</Button><Button colorScheme="brand" onClick={handleSave}>{editMode ? 'Actualizar' : 'Crear'}</Button></ModalFooter>
      </ModalContent></Modal>
    </Box>
  );
}
